'use client'

/**
 * SymbolTape — the live scanner tape on /stocks/[symbol] (owner 2026-07-07 round 3:
 * "all the scanner alerts show the same… will alerts stream into this page?").
 *
 · Every alert TYPE gets its own identity: icon, color, left accent — same design language
 *   as the live alerts page, so the tape reads at a glance (RVOL emerald, movers amber,
 *   Watcher trades gold w/ trade cards, EMA purple, halts red, resumes green).
 * · LIVE: polls every 15s; fresh rows ring in with the type's accent.
 * · SSR: initial alerts arrive as props so crawlers index the same content as before.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Activity, AlertCircle, BarChart3, CheckCircle, Eye, LineChart, TrendingUp,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cleanAlertMessage, parseThothMeta } from '@/lib/alerts/thothMessage'

export type TapeAlert = {
  message: string | null
  triggeredBy: string | null
  alertType: string | null
  rvol: string | number | null
  /** SSR passes drizzle Date objects (RSC serializes Dates); the API returns strings. */
  createdAt: string | Date | null
}

type TradeMeta = {
  side?: string; entryPrice?: number; exitPrice?: number; shares?: number
  realizedPlPct?: number; reason?: string; instance?: string
}

// ⚠ THESE WERE THE ONLY CORRECT COPIES ON THE SITE, and being local is exactly why the other two
// renderers never got them. Now thin wrappers over the shared helper - see lib/alerts/thothMessage.
const parseTradeMeta = (msg: string | null): TradeMeta | null => parseThothMeta(msg) as TradeMeta | null
const cleanMessage = (msg: string | null) => cleanAlertMessage(msg)
const fmtUsd = (n: number) => `$${n < 1 ? n.toFixed(4) : n.toFixed(2)}`

/** Accepts BOTH shapes crossing this boundary: Date (SSR/drizzle via RSC) and naive-UTC
 * string (API JSON) — the launch crash was .endsWith on a Date. */
function toDate(ts: string | Date | null): Date | null {
  if (!ts) return null
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts
  const s = String(ts)
  const d = new Date(s.endsWith('Z') || s.includes('+') ? s : `${s}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

function fmtWhen(ts: string | Date | null): string {
  const d = toDate(ts)
  if (!d) return ''
  return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' ET'
}
/** Relative age ("2d ago") — shown next to the exact ET stamp so alerts read at a glance. */
function fmtAgo(ts: string | Date | null): string {
  const d = toDate(ts)
  if (!d) return ''
  const m = Math.floor((Date.now() - d.getTime()) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

/** Per-type identity — mirrors the live alerts page so the tape reads at a glance. */
function typeStyle(t: string): { Icon: LucideIcon; label: string; badge: string; accent: string; iconTone: string } {
  const k = t.toLowerCase()
  // The Watcher is ONE AI; its paper trades still store the legacy persona names - 'rider' is written
  // LIVE at ~3,600/wk by topPlayAlert.ts and deliberately never renamed (see seo/alertLabel.ts). Match
  // them ALL here, or they fall through to the RVOL default below and the AI's own trades read "RVOL".
  if (k.includes('watcher') || k === 'rider' || k === 'thoth' || k === 'ra' || k === 'osiris' || k === 'solomon') return {
    Icon: Eye, label: 'Watcher',
    badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    accent: 'border-l-amber-400/80', iconTone: 'text-amber-600 dark:text-amber-400',
  }
  if (k.startsWith('ema')) return {
    Icon: k === 'ema_trending' ? BarChart3 : LineChart, label: k === 'ema_trending' ? 'EMA Trend' : 'EMA',
    badge: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30',
    accent: 'border-l-purple-400/70', iconTone: 'text-purple-600 dark:text-purple-400',
  }
  if (k === 'halt') return {
    Icon: AlertCircle, label: 'Halt',
    badge: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
    accent: 'border-l-red-500/80', iconTone: 'text-red-600 dark:text-red-400',
  }
  if (k === 'resume') return {
    Icon: CheckCircle, label: 'Resume',
    badge: 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30',
    accent: 'border-l-green-500/80', iconTone: 'text-green-600 dark:text-green-400',
  }
  if (k === 'mover' || k === 'price' || k.includes('momentum')) return {
    Icon: TrendingUp, label: 'Mover',
    badge: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
    accent: 'border-l-orange-400/80', iconTone: 'text-orange-600 dark:text-orange-400',
  }
  return {
    Icon: Activity, label: 'RVOL',
    badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    accent: 'border-l-emerald-400/80', iconTone: 'text-emerald-600 dark:text-emerald-400',
  }
}

const keyOf = (a: TapeAlert) => `${toDate(a.createdAt)?.getTime() ?? ''}|${(a.message ?? '').slice(0, 60)}`

export default function SymbolTape({ symbol, initialAlerts }: { symbol: string; initialAlerts: TapeAlert[] }) {
  const [alerts, setAlerts] = useState<TapeAlert[]>(initialAlerts)
  const [newKeys, setNewKeys] = useState<Set<string>>(new Set())
  const known = useRef<Set<string>>(new Set(initialAlerts.map(keyOf)))

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`/api/stocks/tape?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      if (!r.ok) return
      const j = await r.json()
      if (!Array.isArray(j?.alerts)) return
      const incoming: TapeAlert[] = j.alerts
      const fresh = incoming.map(keyOf).filter((k) => !known.current.has(k))
      for (const k of fresh) known.current.add(k)
      setAlerts(incoming)
      if (fresh.length) {
        setNewKeys((prev) => new Set([...prev, ...fresh]))
        setTimeout(() => setNewKeys((prev) => {
          const n = new Set(prev)
          for (const k of fresh) n.delete(k)
          return n
        }), 2500)
      }
    } catch { /* keep current */ }
  }, [symbol])

  useEffect(() => {
    const iv = setInterval(() => { if (!document.hidden) refresh() }, 15_000)
    return () => clearInterval(iv)
  }, [refresh])

  if (alerts.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
        No scanner alerts for {symbol} recently - this tape lights up the moment volume or momentum picks up.
      </p>
    )
  }

  return (
    <ul className="space-y-1.5">
      {alerts.map((a) => {
        const k = keyOf(a)
        const t = typeStyle(String(a.alertType ?? a.triggeredBy ?? 'rvol'))
        const trade = parseTradeMeta(a.message)
        const isSell = trade?.side === 'sell'
        const plPct = typeof trade?.realizedPlPct === 'number' && Number.isFinite(trade.realizedPlPct) ? trade.realizedPlPct : null
        const rvolN = a.rvol != null ? Number(a.rvol) : 0
        return (
          <li key={k}
            className={`rounded-lg border border-l-2 bg-card px-3 py-2 transition-all duration-700 ${t.accent} ${
              newKeys.has(k) ? 'border-border ring-1 ring-amber-400/25 bg-accent dark:bg-zinc-900/90' : 'border-border hover:border-muted-foreground/40 dark:hover:border-zinc-700'
            }`}>
            <div className="flex items-start gap-2.5">
              <t.Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${t.iconTone}`} />
              <div className="min-w-0 flex-1">
                <div className="mb-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className={`rounded border px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide ${t.badge}`}>{t.label}</span>
                  {trade?.instance && trade.instance !== 'Watcher' && (
                    <span className="rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-sky-600 dark:text-sky-400/90">dev</span>
                  )}
                  {rvolN > 0 && <span className="font-mono">RVOL {rvolN.toFixed(1)}×</span>}
                  <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap">
                    <span className="font-semibold text-foreground/70">{fmtAgo(a.createdAt)}</span>
                    <span className="text-muted-foreground/60">·</span>
                    <span>{fmtWhen(a.createdAt)}</span>
                  </span>
                </div>
                <p className="text-[13px] leading-snug text-foreground">{cleanMessage(a.message)}</p>
                {trade && (trade.entryPrice != null || trade.exitPrice != null) && (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[11px]">
                    {isSell && trade.entryPrice != null && trade.exitPrice != null ? (
                      <span className="text-foreground/80">
                        {fmtUsd(Number(trade.entryPrice))} <span className="text-muted-foreground">→</span> {fmtUsd(Number(trade.exitPrice))}
                      </span>
                    ) : trade.entryPrice != null ? (
                      <span className="text-foreground/80">entry {fmtUsd(Number(trade.entryPrice))}</span>
                    ) : null}
                    {plPct != null && (
                      <span className={`rounded px-1.5 py-0.5 font-bold tabular-nums ${plPct >= 0 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/15 text-rose-600 dark:text-rose-300'}`}>
                        {plPct >= 0 ? '+' : ''}{plPct.toFixed(2)}%
                      </span>
                    )}
                    {trade.reason && <span className="min-w-0 truncate text-[10px] text-muted-foreground">{trade.reason}</span>}
                  </div>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
